import * as React from 'react';
import { cn } from '@antimatterhq/ui/lib/utils';

export interface ProgressRingIconProps {
  progress: number; // 0-100
  size?: number;
  className?: string;
  strokeWidth?: number;
  backgroundColor?: string;
  progressColor?: string;
}

export const ProgressRingIcon = React.forwardRef<
  SVGSVGElement,
  ProgressRingIconProps
>(
  (
    {
      progress,
      size = 24,
      className,
      strokeWidth = 2,
      backgroundColor = '#9CA3AF', // grey-400
      progressColor = '#10B981', // green-500
    },
    ref,
  ) => {
    // Clamp progress between 0 and 100
    const clampedProgress = Math.max(0, Math.min(100, progress));

    // Calculate radius and circumference
    const radius = (size - strokeWidth) / 2;
    const circumference = 2 * Math.PI * radius;

    // Calculate stroke dash offset for progress
    // Start from 0 (full circle) and reduce based on progress
    const strokeDashoffset =
      circumference - (clampedProgress / 100) * circumference;

    return (
      <svg
        ref={ref}
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        className={cn('transition-all duration-300', className)}
      >
        {/* Background circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={backgroundColor}
          strokeWidth={strokeWidth}
          opacity={0.3}
        />

        {/* Progress circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={progressColor}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          className="transition-all duration-300 ease-out"
        />
      </svg>
    );
  },
);

ProgressRingIcon.displayName = 'ProgressRingIcon';
